import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IconPlus } from '@tabler/icons-react';

import { useSolutionDisplay } from './hooks/useSolutionDisplay';
import { ErrorMessage, LoadingState, EmptyState } from './components/SolutionStates';
import { SolutionCard } from './components/SolutionCard';
import './SolutionDisplay.css';

const SolutionDisplay = ({ postId, onSolutionCountChange }) => {
  const location = useLocation();
  const hookData = useSolutionDisplay({ postId, onSolutionCountChange });

  if (!postId) {
    return <ErrorMessage message="Error: Missing post ID" />;
  }

  if (hookData.loading && (!hookData.solutions || hookData.solutions.length === 0)) {
    return <LoadingState />;
  }

  if (hookData.error && (!hookData.solutions || hookData.solutions.length === 0)) {
    return <ErrorMessage message={hookData.error} onRetry={hookData.handleRefresh} />;
  }

  if (!hookData.solutions || hookData.solutions.length === 0) {
    return <EmptyState postId={postId} />;
  }

  return (
    <div className="solutions-container">
      <div className="solutions-header" ref={hookData.headerRef}>
        <h2 className="text-xl font-semibold text-gray-800 mb-3">Solutions ({hookData.totalCount || 0})</h2>
        <div className="solutions-actions flex gap-3">
          <button
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            onClick={hookData.handleRefresh}
            disabled={hookData.loading}
          >
            {hookData.loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <Link
            to={`/solutions/${postId}/build`}
            state={{
              from: location.pathname,
              scrollToPost: postId,
              shouldExpandSolutions: true
            }}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <IconPlus size={16} /> Add Solution
          </Link>
        </div>
      </div>

      <div className="solutions-list space-y-3 mt-2" ref={hookData.listRef}>
        {hookData.solutions.slice(0, Math.max(0, hookData.visibleCount)).map(solution => (
          <SolutionCard
            key={solution.id}
            solution={solution}
            currentUser={hookData.currentUser}
            usersEntities={hookData.usersEntities}
            highlightedId={hookData.highlightedId}
            liking={hookData.liking}
            saving={hookData.saving}
            sharing={hookData.sharing}
            expandedPhases={hookData.expandedPhases}
            setExpandedPhases={hookData.setExpandedPhases}
            handleSolutionClick={hookData.handleSolutionClick}
            handleProfileClick={hookData.handleProfileClick}
            handleLike={hookData.handleLike}
            handleSave={hookData.handleSave}
            handleShare={hookData.handleShare}
          />
        ))}
      </div>
      {(() => {
        const canShowLess = hookData.visibleCount > hookData.PAGE_SIZE && hookData.solutions.length > 0;
        const canShowMore = hookData.hasMore || ((hookData.totalCount || 0) > hookData.visibleCount);
        if (!canShowLess && !canShowMore) return null;
        return (
          <div className="mt-4 flex justify-center gap-3">
            {canShowLess && (
              <button
                onClick={hookData.handleShowLess}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-60"
              >
                Show less
              </button>
            )}
            {canShowMore && (
              <button
                onClick={hookData.handleLoadMore}
                disabled={hookData.loadingMore || hookData.loading}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-60 rounded-md shadow-sm"
              >
                {(hookData.loading || (hookData.loadingMore && hookData.visibleCount >= hookData.solutions.length)) ? 'Loading...' : 'Show more'}
              </button>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default SolutionDisplay;
